/**
 * Страница «Цифровой паспорт» (FE-6 каркас, FE-7/8/9 контент).
 *
 * Состав вкладки зависит от уровня КАТО (US-11 FR-1.1):
 *
 * - **Район / город областного значения** — районная раскладка (US-11,
 *   DP-412): во всю ширину сверху «Административное деление»; ниже две
 *   колонки — слева «Население» → «Численность по населённым пунктам» →
 *   «Медиамониторинг», справа «Показатели СЭР» → «Индекс потребительских
 *   цен» → «Бюджет района» (FR-1.3). Медиамониторинг и ИПЦ сохраняются на
 *   этом уровне дополнительно к пяти новым блокам (решение 6 от 07.09).
 * - **Область, сельский округ, село** — прежний состав: ряд стат-карточек
 *   из `general`, «Население» (адм. деление) и «Показатели СЭР» в первом
 *   ряду, «Медиамониторинг» и «Национальный состав» (donut) — во втором.
 *
 * Блоки районной раскладки, у которых источника ещё нет (счётчики
 * деления US-12 FR-5, ИПЦ FR-6Б, бюджет FR-9), отрисовываются в состоянии
 * «нет данных» — моков и демо-значений у них нет намеренно.
 *
 * Маршрут: `/:kato/digital-passport`. KATO из URL. Видимость блока —
 * `useModulePresent` (FE-11 заменит на реальный context).
 *
 * Один фетч `/api/public/general` на страницу: children + nationality
 * сразу попадают в блоки через props. Медиамониторинг (FE-9) фетчит
 * отдельный endpoint `/api/public/media-monitoring`.
 */
import { Monitor } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, useParams } from 'react-router-dom';

import { AdministrativeDivision } from './components/administrative-division/AdministrativeDivision';
import { CpiTable } from './components/cpi/CpiTable';
import { DistrictBudget } from './components/district-budget/DistrictBudget';
import { DistrictDivision } from './components/district-division/DistrictDivision';
import { MediaMonitoringBlock } from './components/media-monitoring/MediaMonitoring';
import { PopulationSplit } from './components/population-split/PopulationSplit';
import { SettlementPopulation } from './components/settlement-population/SettlementPopulation';
import { PopulationNationalityBlock } from './components/population-nationality/PopulationNationality';
import { SerIndicatorCards } from './components/ser-indicators/SerIndicatorCards';
import {
  SER_SUBTYPE_BY_KEY,
  SerTable,
  type SerTableKey,
  type SerTableValue,
} from './components/ser-table/SerTable';
import { BlockLoader } from '../../components/common/BlockLoader';
import { WidgetErrorBoundary } from '../../components/common/WidgetErrorBoundary';
import { useKato } from '../../contexts/KatoContext';
import { useUser } from '../../contexts/UserContext';
import { useYear } from '../../contexts/YearContext';
import { isHardDataKato } from '../../hardData/constants';
import populationHard from '../../hardData/population.json';
import serCardsHard from '../../hardData/serCards.json';
import serTableHard from '../../hardData/serTable.json';
import { useModulePresent } from '../../hooks/useModulePresent';
import { isDistrictPassportLevel } from '../../lib/kato-level';
import { isKatoInScope } from '../../lib/user-access';
import { getGeneral } from '../../requests/public.request';
import type { PublicGeneralResponse } from '../../types/public';

export const DigitalPassportPage = () => {
  const { t } = useTranslation();
  const { kato: katoParam } = useParams<{ kato: string }>();
  const { selectedKato, setSelectedKato } = useKato();
  const { user } = useUser();
  const { selectedYear } = useYear();
  const showAdmin = useModulePresent(
    'digital-passport.administrative-division',
  );
  const showNationality = useModulePresent(
    'digital-passport.population-nationality',
  );
  const showMedia = useModulePresent('digital-passport.media-monitoring');
  const showSerTable = useModulePresent('digital-passport.ser-table');
  // Блоки районной вкладки (US-11). Идентификаторов ещё нет в каталоге
  // бэкенда (MODULE_SEED) — до их появления useModulePresent отдаёт
  // default-visible, тумблеров у блоков не будет (US-09 §4).
  const showDistrictDivision = useModulePresent(
    'digital-passport.district-division',
  );
  const showDistrictPopulation = useModulePresent(
    'digital-passport.district-population',
  );
  const showSettlements = useModulePresent(
    'digital-passport.settlement-population',
  );
  const showCpi = useModulePresent('digital-passport.cpi');
  const showBudget = useModulePresent('digital-passport.district-budget');

  const [data, setData] = useState<PublicGeneralResponse | null>(null);
  const [loading, setLoading] = useState(false);
  // «Кликабельные публикации» — количество постов медиамониторинга с url;
  // поднимает MediaMonitoringBlock через onPublications.
  const [clickablePubs, setClickablePubs] = useState<number | null>(null);

  const katoNum = katoParam ? Number.parseInt(katoParam, 10) : NaN;
  const katoValid = Number.isFinite(katoNum);
  // КАТО приходит из URL, а он переживает и смену учётки, и закладку. Чужой
  // КАТО дал бы 403 по каждому виджету — страница выглядела бы «пустой», а не
  // «недоступной».
  const katoAllowed = katoValid && isKatoInScope(katoNum, user?.kato_access);

  // Sync URL kato → KatoContext so ContextSidebar and other consumers stay in sync.
  useEffect(() => {
    if (katoAllowed) setSelectedKato(katoNum);
  }, [katoNum, katoAllowed, setSelectedKato]);

  useEffect(() => {
    // /public/general запрашивается и на демо-областях: нацсостав всегда
    // рисуется с бэка (замечание ревью от 24.08.2026), моки hardData
    // остаются только у остальных блоков.
    if (!katoValid) return;
    let cancelled = false;
    setLoading(true);
    // Смена территории — прошлый счётчик публикаций больше не актуален.
    setClickablePubs(null);
    getGeneral(katoNum, selectedYear ?? undefined)
      .then((response) => {
        if (!cancelled) setData(response);
      })
      .catch(() => {
        if (!cancelled) setData(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [katoValid, katoNum, selectedYear]);

  if (!katoValid) {
    return <Navigate to="/" replace />;
  }

  // Не своя территория — уводим на доступную, а не показываем пустые виджеты.
  if (!katoAllowed && selectedKato !== null && selectedKato !== katoNum) {
    return <Navigate to={`/${selectedKato}/digital-passport`} replace />;
  }

  // Демо-режим (области из HARD_DATA_KATOS): блоки показывают моки
  // hardData вместо запросов к бэку.
  const isHardKato = isHardDataKato(katoNum);
  // Областной КАТО (XX0000000): объёмы СЭР — в млрд тг, ниже — в млн тг.
  const isOblastKato = katoNum % 10000000 === 0;
  // Район / город областного значения — районный состав вкладки (US-11
  // FR-1.1). Для округа и села состав вкладки не меняется.
  const isDistrictLevel = isDistrictPassportLevel(katoNum);

  // Таблица «Показатели СЭР» — из basic_stats type='SER' (/public/general);
  // ключ строки → subtype живёт рядом с виджетом (SER_SUBTYPE_BY_KEY).
  /** ИФО показателя СЭР по subtype (basic_stats type='SER'). */
  const serIfo = (subtype: string): number | null =>
    data?.serIndicators.find((s) => s.subtype === subtype)?.ifo ?? null;

  const serTableValues = Object.fromEntries(
    (
      Object.entries(SER_SUBTYPE_BY_KEY) as Array<[SerTableKey, string]>
    ).flatMap(
      ([key, subtype]) => {
        const stat = data?.serIndicators.find((s) => s.subtype === subtype);
        if (!stat) return [];
        // «Динамика» — изменение к прошлому году в %:
        // (current − previous) / previous × 100.
        const change =
          stat.currentYearVal !== null &&
          stat.previousYearVal !== null &&
          stat.previousYearVal !== 0
            ? ((stat.currentYearVal - stat.previousYearVal) /
                stat.previousYearVal) *
              100
            : null;
        return [
          [key, { ifo: stat.ifo, change, amountMln: stat.currentYearVal }],
        ];
      },
    ),
  ) as Partial<Record<SerTableKey, SerTableValue>>;

  // «Рост населения» — из population_dynamics (отдельного поля в /general
  // нет намеренно): текущий год к прошлому, в %.
  const populationGrowth = (() => {
    const points = data?.populationDynamics ?? [];
    const year = data?.general.year ?? null;
    if (year === null) return null;
    const current = points.find((p) => p.year === year)?.value ?? null;
    const previous = points.find((p) => p.year === year - 1)?.value ?? null;
    return current !== null && previous !== null && previous !== 0
      ? Math.round(((current - previous) / previous) * 1000) / 10
      : null;
  })();

  return (
    <article
      className="space-y-3.5 p-5"
      data-testid="digital-passport-page"
      data-kato={katoNum}
    >
      <div className="flex items-center gap-4 py-3">
        <h1 className="shrink-0 text-xl font-extrabold">
          {t('digitalPassport.title')}
        </h1>
        <div className="h-px flex-1 bg-surface-border" aria-hidden="true" />
        <Monitor
          className="size-6 shrink-0 text-brand-600"
          strokeWidth={1.8}
          aria-hidden="true"
        />
      </div>

      {loading && !data && <BlockLoader testId="dp-loading" />}

      {/* Демо-область данных с бэка не ждёт — подпись «недоступны» не про неё. */}
      {!loading && !data && !isHardKato && (
        <p className="text-sm text-text-muted" data-testid="dp-no-data">
          {t('common.dataUnavailable')}
        </p>
      )}

      {/* Районная раскладка (US-11 FR-1.3): во всю ширину сверху —
          административное деление; ниже две колонки. Слева Население →
          Численность по НП → Медиамониторинг, справа Показатели СЭР →
          ИПЦ → Бюджет района. Выключенный блок места не занимает —
          остальные в своей колонке смыкаются. */}
      {isDistrictLevel ? (
        <>
          {/* Для города областного значения блок не выводится вовсе
              (FR-2.5). Признак города по коду КАТО не определяется — он
              должен прийти из данных (US-12, сценарий 11), поэтому пока
              блок выводится и для городов. */}
          {showDistrictDivision && (
            <WidgetErrorBoundary key={`district-division-${katoNum}`}>
              {/* Счётчики состава района — отдельная операция US-12 FR-5,
                  её ещё нет: без источника блок в состоянии «нет данных».
                  Считать округа и сёла из children нельзя — FR-2.2
                  требует выводить значения как есть. */}
              <DistrictDivision counts={null} />
            </WidgetErrorBoundary>
          )}

          <div className="grid grid-cols-1 gap-3.5 xl:grid-cols-2">
            <div className="space-y-3.5">
              {showDistrictPopulation && (
                <WidgetErrorBoundary key={`district-population-${katoNum}`}>
                  {/* Итог — general.population из /public/general. Разбивки
                      город/село по методологии БНС в контракте пока нет
                      (US-12 FR-6), поэтому блок показывает только общую
                      численность — так требует FR-3.5. Разбивку из
                      «Мониторинга СНП» подставлять нельзя (FR-3.4). */}
                  <PopulationSplit
                    total={data?.general.population ?? null}
                    urban={null}
                    rural={null}
                  />
                </WidgetErrorBoundary>
              )}
              {showSettlements && (
                <WidgetErrorBoundary key={`settlements-${katoNum}`}>
                  {/* children из /public/general: наименование + численность.
                      Признак города районного значения (FR-4.2) контракт не
                      отдаёт — до US-12 список идёт по убыванию численности. */}
                  <SettlementPopulation items={data?.children ?? []} />
                </WidgetErrorBoundary>
              )}
              {showMedia && (
                <WidgetErrorBoundary key={`media-${katoNum}`}>
                  {/* Блок сохраняется на районном уровне (FR-6А, решение 6):
                      тот же виджет, меняется только КАТО запроса. */}
                  <MediaMonitoringBlock
                    kato={katoNum}
                    onPublications={(pubs) =>
                      setClickablePubs(
                        pubs === null
                          ? null
                          : pubs.filter((pub) => pub.url).length,
                      )
                    }
                  />
                </WidgetErrorBoundary>
              )}
            </div>
            <div className="space-y-3.5">
              {showSerTable && (
                <WidgetErrorBoundary key={`district-ser-${katoNum}`}>
                  {/* Состав строк и глубина drill-through пока прежние
                      (US-02): районный набор отраслей FR-5.1 и переход на
                      страницу отрасли в «Аналитике» FR-5.6 требуют
                      контракта US-12. */}
                  <SerTable
                    values={serTableValues}
                    amountUnit="mln"
                    kato={katoNum}
                  />
                </WidgetErrorBoundary>
              )}
              {showCpi && (
                <WidgetErrorBoundary key={`cpi-${katoNum}`}>
                  {/* ИПЦ в районном разрезе статистикой не публикуется —
                      уровень блока «по области» (FR-6Б.4). Источника в
                      контракте ещё нет → «нет данных». */}
                  <CpiTable level="oblast" />
                </WidgetErrorBoundary>
              )}
              {showBudget && (
                <WidgetErrorBoundary key={`district-budget-${katoNum}`}>
                  {/* Операции бюджета района (US-12 FR-9) ещё нет;
                      бюджетные ручки аналитики дают другой состав строк,
                      чем требует FR-6.4 → «нет данных». */}
                  <DistrictBudget />
                </WidgetErrorBoundary>
              )}
            </div>
          </div>
        </>
      ) : (
        <>
        {/* Всегда видимы: без данных — базовые карточки (название + иконка).
            Демо-КАТО — мок serCards.json; остальные — /public/general:
            ИФО инвестиций/промышленности — basic_stats type='SER'
            (INVESTMENT / MANUFACTURING_INDUSTRY), тренды в API пока
            нет → чипы скрыты. */}
        <SerIndicatorCards
          values={
            isHardKato
              ? serCardsHard.indicators
              : {
                  population: {
                    value: data?.general.population ?? null,
                    trend: null,
                  },
                  clickablePublications: {
                    value:
                      clickablePubs ??
                      data?.serStats.clickablePublications ??
                      null,
                    trend: null,
                  },
                  ifoInvestments: {
                    value:
                      serIfo('INVESTMENT') ??
                      data?.serStats.ifoInvestments ??
                      null,
                    trend: null,
                  },
                  ifoIndustry: {
                    value:
                      serIfo('INDUSTRIAL_PRODUCTION') ??
                      data?.serStats.ifoIndustry ??
                      null,
                    trend: null,
                  },
                }
          }
        />

        {/* Без items-start: колонки растягиваются до высоты ряда,
            карточки внутри — h-full → блоки одной высоты. */}
        <div className="grid grid-cols-1 gap-3.5 xl:grid-cols-2">
          <div className="space-y-3.5">
            {showAdmin && (
              <WidgetErrorBoundary key={`admin-${katoNum}`}>
                <AdministrativeDivision
                  items={
                    isHardKato ? populationHard.items : (data?.children ?? [])
                  }
                  // Рост населения: демо — мок; остальные — расчёт из
                  // population_dynamics (текущий год к прошлому).
                  growthPercent={
                    isHardKato ? populationHard.growthPercent : populationGrowth
                  }
                />
              </WidgetErrorBoundary>
            )}
          </div>
          <div className="space-y-3.5">
            <WidgetErrorBoundary key={`ser-table-${katoNum}`}>
              {/* Демо-КАТО — мок serTable.json; остальные — basic_stats
                  type='SER' из /public/general (см. serIndicators). */}
              <SerTable
                values={isHardKato ? serTableHard.indicators : serTableValues}
                amountUnit={isOblastKato ? 'mlrd' : 'mln'}
                // Drill-through (FR-4.6) — только на данных бэка: в демо-режиме
                // первичных записей нет.
                kato={isHardKato ? null : katoNum}
              />
            </WidgetErrorBoundary>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-3.5 xl:grid-cols-2">
          {showMedia && (
            <WidgetErrorBoundary key={`media-${katoNum}`}>
              <MediaMonitoringBlock
                kato={katoNum}
                onPublications={(pubs) =>
                  setClickablePubs(
                    pubs === null ? null : pubs.filter((p) => p.url).length,
                  )
                }
              />
            </WidgetErrorBoundary>
          )}
          {showNationality && (
            <WidgetErrorBoundary key={`nationality-${katoNum}`}>
              {/* Всегда из бэка; нет данных → placeholder (демо-мока нет). */}
              <PopulationNationalityBlock
                nationality={data?.nationality ?? null}
              />
            </WidgetErrorBoundary>
          )}
        </div>
        </>
      )}
    </article>
  );
};
